import { Check, Pencil, Trash, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useManageAsesmenStore } from "../manage-asesmen.store";
import Pagination from "../../../components/tables/pagination";
import AddButton from "../../../components/buttons/add";
import Table from "../../../components/tables/base";
import { AssessmentLocation, PsikologRegion } from "../manage-asesmen";
import { District } from "../../../types/common";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import BaseModal from "../../../components/modal/base";
import { FormInput } from "../../../components/forms/input";
import { request } from "../../../api/config";
import { FormSelectAsync } from "../../../components/forms/input-select";
import { Button } from "../../../components/buttons";
import { Spinner } from "flowbite-react";
import { FormTextArea } from "../../../components/forms/input-textarea";
import { useAlert } from "../../../stores/alert";
import {
  createLocation,
  deleteLocation,
  updateLocation,
} from "../manage-asesmen.service";
import ModalDeleteConfirmation from "../../../components/modal/delete-confirmation";
import { twMerge } from "tailwind-merge";

type FormValues = {
  psikolog_id: PsikologRegion | { id: string; name: string } | null;
  district_id: District | null;
  postal_code: string;
  address: string;
  gmaps_link: string;
  lat: string;
  lng: string;
  phone: string;
  notes: string;
};

type ErrorForm = {
  psikolog_id: [] | null;
  district_id: [] | null;
  postal_code: [] | null;
  address: [] | null;
  gmaps_link: [] | null;
  lat: [] | null;
  lng: [] | null;
  phone: [] | null;
  notes: [] | null;
};

const AssessmentPlace = () => {
  const [modal, setModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | undefined>(
    undefined
  );
  const [selected, setSelected] = useState<AssessmentLocation | undefined>();
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorForm | null>(null);
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>("");
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [loadingApproval, setLoadingApproval] = useState<boolean>(false);
  const [modalApprove, setModalApprove] = useState<boolean>(false);

  const { locations, getLocations, loadingLocations } = useManageAsesmenStore();

  const forms = useForm<FormValues>();
  const { setMessage } = useAlert();

  const selectPsikologs = async (inputValue: string) => {
    let params = {
      q: inputValue,
    };
    const { data } = await request.get("/select/psikolog-region", {
      params: params,
    });
    return data.data.data;
  };

  const psikologId = useWatch({
    control: forms.control,
    name: "psikolog_id",
  });

  const selectRegion = async (inputValue: string) => {
    let params = {
      psikolog_id: forms.watch("psikolog_id.id") ?? "",
      q: inputValue,
    };
    const { data } = await request.get("/select/district-by-psikolog-region", {
      params: params,
    });
    return data.data;
  };

  const handleNext = () => {
    if (page === locations?.last_page) {
      return;
    }

    setPage(page + 1);
  };

  const handlePrevious = () => {
    if (page === 1) {
      return;
    }

    setPage(page - 1);
  };

  const handleFormEdit = (item: AssessmentLocation) => {
    forms.setValue("psikolog_id", item.psikolog);
    forms.setValue("district_id", item.district_id);
    forms.setValue("address", item.address);
    forms.setValue("notes", item.notes ?? "");
    forms.setValue("gmaps_link", item.gmaps_link ?? "");
    forms.setValue("phone", item.phone ?? "");
  };

  const handleSave = forms.handleSubmit(async (data) => {
    setLoadingSubmit(true);
    try {
      if (modalMode === "create") {
        await createLocation({
          ...data,
          psikolog_id: data.psikolog_id?.id,
          district_id: data.district_id?.id,
        });
      }

      if (modalMode === "edit") {
        console.log("here");
        selected &&
          (await updateLocation(selected?.id, {
            ...data,
            psikolog_id: data.psikolog_id?.id,
            district_id: data.district_id?.id,
          }));
      }
      setMessage("Data berhasil disimpan", "success");
      getLocations();
      forms.reset({
        psikolog_id: null,
        district_id: null,
        address: "",
        notes: "",
        gmaps_link: "",
        phone: "",
      });
      setModal(false);
    } catch (err: any) {
      setErrors(err.response.data.errors);
      setMessage("Oops, ada yang salah di server", "error");
    } finally {
      setLoadingSubmit(false);
    }
  });

  const handleApprove = async () => {
    try {
      setLoadingApproval(true);
      selected &&
        (await request.patch(`/manage-asesmen/place/${selected.id}/approve`));
      setMessage("Lokasi berhasil di verifikasi", "success");
      setSelected(undefined);
      setModalApprove(false);
      getLocations();
    } catch (error) {
      setMessage("Oops, ada yang error", "error");
    } finally {
      setLoadingApproval(false);
    }
  };

  const handleDelete = async () => {
    setLoadingSubmit(true);
    try {
      selected && (await deleteLocation(selected?.id));
      setSelected(undefined);
      setModalDelete(false);
      setMessage("Data berhasil dihapus", "success");
      getLocations();
    } catch (err: any) {
      setErrors(err.response.data.errors);
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    getLocations({ q, page });
  }, [getLocations, page, q]);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <strong className="text-xl">Lokasi Asesmen</strong>
        <div className="relative">
          <input
            className="w-full border rounded-lg p-2 text-sm pr-12"
            placeholder="Cari.."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <div className="absolute top-2.5 right-2">
              <button
                className="text-red-600 hover:text-red-700"
                onClick={() => setQ("")}
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      <AddButton
        onClick={() => {
          setModal(true);
          setModalMode("create");
          forms.reset({
            psikolog_id: null,
            district_id: null,
            address: "",
            gmaps_link: "",
            notes: "",
            phone: "",
          });
        }}
      />
      <Table>
        <Table.Thead>
          <Table.Th>#</Table.Th>
          <Table.Th>Psikolog</Table.Th>
          <Table.Th>Region</Table.Th>
          <Table.Th>Kecamatan / Kota / Alamat</Table.Th>
          <Table.Th className="text-center">Opsi</Table.Th>
        </Table.Thead>
        <Table.Tbody>
          {loadingLocations ? (
            <Table.TrLoading cols={5} rows={4} />
          ) : (
            <>
              {locations?.data?.length === 0 ? (
                <Table.Tr>
                  <Table.Td cols={5} className="text-center py-3">
                    Tidak ada data ditemukan!
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {locations?.data?.map((item, key) => (
                    <Table.Tr key={key}>
                      <Table.Td>
                        {(
                          key +
                          1 +
                          locations.per_page * (locations.current_page - 1)
                        ).toString()}
                      </Table.Td>
                      <Table.Td>
                        <>
                          <span>{item.psikolog?.name ?? "-"}</span>
                          <div
                            className={twMerge(
                              "text-xs ",
                              item.approved_at
                                ? " text-green-600"
                                : " text-yellow-600"
                            )}
                          >
                            {item.approved_at ? "Approved" : "Pending Approval"}
                          </div>
                        </>
                      </Table.Td>
                      <Table.Td>
                        {item.offline_assessment_region?.name}
                      </Table.Td>
                      <Table.Td className="w-[50%]">
                        <strong className="block">
                          {item.district_id.name ?? ""} /{" "}
                          {item.district_id?.city?.name ?? "-"}
                          {" / "}
                          {item.district_id?.city?.province?.name ?? "-"}
                        </strong>
                        <small className="block leading-3">
                          {item.address ?? ""}
                        </small>
                      </Table.Td>

                      <Table.Td>
                        <div className="flex items-center justify-center gap-1">
                          {!item.approved_at && (
                            <Check
                              className="text-green-600 text-xl cursor-pointer"
                              onClick={() => {
                                setModalApprove(true);
                                setSelected(item);
                              }}
                            />
                          )}
                          <Trash
                            className="text-red-600 text-xl cursor-pointer"
                            onClick={() => {
                              setSelected(item);
                              setModalDelete(true);
                            }}
                          />
                          <Pencil
                            className="text-blue-600 text-xl cursor-pointer"
                            onClick={() => {
                              handleFormEdit(item);
                              setSelected(item);
                              setModal(true);
                              setModalMode("edit");
                            }}
                          />
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </>
              )}
            </>
          )}
        </Table.Tbody>
      </Table>
      <Pagination
        currentPage={locations?.current_page ?? 1}
        totalPage={locations?.last_page ?? 1}
        onNext={handleNext}
        onPrevious={handlePrevious}
      />

      <BaseModal
        title={
          modalMode === "create"
            ? "Tambah Lokasi Asesmen Offline"
            : "Edit Lokasi Asesmen Offline"
        }
        isOpen={modal}
        close={() => setModal(false)}
      >
        <FormProvider {...forms}>
          <form>
            <FormSelectAsync
              label="Pilih Psikolog"
              name="psikolog_id"
              control={forms.control}
              loadOption={selectPsikologs}
              required
              optionLabel={(option: PsikologRegion) => `${option.name}`}
              optionValue={(option: PsikologRegion) => `${option.id}`}
              onChange={() => forms.setValue("district_id", null)}
              error={errors?.psikolog_id}
            />

            {psikologId && (
              <FormSelectAsync
                key={psikologId.id}
                label="Pilih Kecamatan"
                name="district_id"
                control={forms.control}
                loadOption={selectRegion}
                required
                optionLabel={(option: District) => (
                  <>
                    <strong>{option.name}</strong>
                    <small className="block leading-none">{`${
                      option.city?.name ?? "-"
                    } / ${option.city?.province?.name ?? "-"}`}</small>
                  </>
                )}
                optionValue={(option: District) => `${option.id}`}
                error={errors?.psikolog_id}
              />
            )}

            <FormTextArea
              name="address"
              control={forms.control}
              label="Alamat"
              error={errors?.address}
              required
            />

            <FormInput
              name="gmaps_link"
              control={forms.control}
              label="Link / URL Google Maps"
              error={errors?.gmaps_link}
            />

            <FormInput
              name="phone"
              control={forms.control}
              label="Nomor HP/Telp"
              error={errors?.phone}
            />

            <FormInput
              name="notes"
              control={forms.control}
              label="Catatan"
              error={errors?.notes}
            />

            <div className="mt-3 flex items-center justify-end">
              <Button className="px-8" onClick={handleSave}>
                {loadingSubmit ? <Spinner /> : "Simpan"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </BaseModal>

      <ModalDeleteConfirmation
        isOpen={modalDelete}
        close={() => setModalDelete(false)}
        name={`Lokasi dari Psikolog ${selected?.psikolog?.name ?? ""}`}
        loading={loadingSubmit}
        action={handleDelete}
      />

      <BaseModal
        title="Verifikasi Lokasi"
        isOpen={modalApprove}
        close={() => setModalApprove(false)}
      >
        <span>Yakin ingin memverifikasi lokasi ini?</span>
        <div className="py-2 flex items-center justify-end mt-4">
          <div className="flex gap-2">
            <Button
              className="bg-red-600 px-4"
              onClick={() => setModalApprove(false)}
            >
              Batal
            </Button>
            <Button
              className="px-12"
              onClick={handleApprove}
              disabled={loadingApproval}
            >
              {loadingApproval ? <Spinner /> : <>Iya, Yakin!</>}
            </Button>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

export default AssessmentPlace;
